import { GenericType } from "@sbd-doug-frontend/shared/lib/ui/quick-search/quick-search.types";
import { CIPXQuoteRequestIntakeForm, CIPXQuoteRequestIntakeFormBondFamily, CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration, CIPXQuoteRequestIntakeFormStateBond, CIPXQuoteRequestIntakeFormStateBondDetailedQuestion } from "./intake-form.types";
import { FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { conditionalRequiredValidator } from "@sbd-doug-frontend/shared/lib/public-validators";
import { removeAllControls } from "@sbd-doug-frontend/shared/lib/public-forms";
import { AbstractControl, ValidationErrors } from "@angular/forms";
import { CIPXQuoteRequestIntakeFormStateBondQuestionValidationRule, CIPXQuoteRequestIntakeFormStateBondQuestionValidationSuccess } from "./intake-form.types";

export function isCIPXQuoteRequestIntakeFormBondFamily(
  obj: any
): obj is CIPXQuoteRequestIntakeFormBondFamily {
  // Check the object is not null and is an object
  if (obj == null || typeof obj !== 'object') {
    return false;
  }

  // Check required fields
  return (
    typeof (obj as CIPXQuoteRequestIntakeFormBondFamily).bond_family_label === 'string' &&
    typeof (obj as CIPXQuoteRequestIntakeFormBondFamily).grp === 'string'
  );
}

export function isCIPXQuoteRequestIntakeFormStateBond(
  obj: any
): obj is CIPXQuoteRequestIntakeFormStateBond {
  // Check the object is not null and is an object
  if (obj == null || typeof obj !== 'object') {
    return false;
  }

  // Check required fields
  return (
    typeof (obj as CIPXQuoteRequestIntakeFormStateBond).state_bond_id === 'number'
  );
}

export function isGenericType(obj: any): obj is GenericType {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    typeof obj.label === 'string' &&
    typeof obj.value === 'string'
  );
}

export function requiresCredit(stateBond: CIPXQuoteRequestIntakeFormStateBond): boolean {
  return stateBond.requires_credit_check;
}

export function configureFormValidationByStateBondRequirements(
  form: FormGroup<CIPXQuoteRequestIntakeForm>,
  stateBond: CIPXQuoteRequestIntakeFormStateBond
): FormGroup<CIPXQuoteRequestIntakeForm> {
  // Remove all existing additional question controls.
  removeAllControls(form.controls.additional_questions);

  if (!stateBond.additional_questions) {
    return form;
  }

  const additionalQuestionsControl = form.controls.additional_questions;

  // Iterate through sections
  stateBond.additional_questions.forEach(section => {
    // Iterate through question sets within each section
    section.question_sets.forEach(questionSet => {
      if (!questionSet.questions || questionSet.questions.length === 0) {
        return;
      }

      questionSet.questions.forEach(question => {
        const validators: ValidatorFn[] = [];

        // Handle default validation rule if present
        if (question.validation) {
          const defaultRule = question.validation;

          // Apply default validation rule
          if (defaultRule.validation_rule) {
            switch (defaultRule.validation_rule) {
              case 'notempty':
                validators.push(Validators.required);
                break;
              case 'txtaxpayerid':
                validators.push(Validators.pattern(/^\d{11}$/));
                break;
              case 'fein':
                validators.push(Validators.pattern(/^\d{2}-?\d{7}$/));
                break;
              case 'explainerquestion':
                if (defaultRule.arguments) {
                  const dependentKeys = defaultRule.arguments
                    .map(arg => arg.question_code)
                    .filter(key => key !== undefined);
                  validators.push(conditionalRequiredValidator(dependentKeys, additionalQuestionsControl));
                }
                break;
            }
          }

          // Handle min/max constraints if present
          if (defaultRule.min !== undefined) {
            validators.push(Validators.min(defaultRule.min));
          }
          if (defaultRule.max !== undefined) {
            validators.push(Validators.max(defaultRule.max));
          }

          // Handle conditional rules if present
          if (defaultRule.rules) {
            defaultRule.rules.forEach(ruleItem => {
              if (!ruleItem.rule || !ruleItem.on_success) {
                return;
              }

              const condition = createConditionFromRule(ruleItem.rule);
              if (condition) {
                const successAction = createSuccessAction(ruleItem.on_success);
                if (successAction) {
                  validators.push(createConditionalValidator(condition, successAction, additionalQuestionsControl));
                }
              }
            });
          }
        }

        // Create the control with the proper type and initial value
        let control;
        console.log('question.question_type=', question.question_type, 'stateBond.state_abbreviation=', stateBond.state_abbreviation, 'question.question_code=', question.question_code);
        if (question.question_type === 'bool') {
          control = new FormControl<boolean | null>(false, validators);
        } else if (question.question_type === 'string') {
          control = new FormControl<string | null>(null, validators);
        } else if (question.question_type === 'number' || question.question_type === 'integer' || question.question_type === 'decimal' || question.question_type === 'float' || question.question_type === 'int') {
          control = new FormControl<number | null>(null, validators);
        } else if (question.question_type === 'user_selected_option') {
          // For select-like controls store the selected string value
          control = new FormControl<string | null>(null, validators);
        } else {
          control = new FormControl<string>('', validators);
        }

        additionalQuestionsControl.addControl(question.question_code, control);
      });
    });
  });

  return form;
}

// Helper function to create a condition from a rule
function createConditionFromRule(rule: CIPXQuoteRequestIntakeFormStateBondQuestionValidationRule): any {
  if (!rule.type || !rule.source || !rule.assessed_value) {
    return null;
  }

  return {
    source: rule.source,
    type: rule.type,
    value: rule.assessed_value
  };
}

// Helper function to create a success action
function createSuccessAction(onSuccess: CIPXQuoteRequestIntakeFormStateBondQuestionValidationSuccess): any {
  if (!onSuccess.validation_rule) {
    return null;
  }

  return {
    validation_rule: onSuccess.validation_rule,
    arguments: onSuccess.arguments
  };
}

// Helper function to create a conditional validator
function createConditionalValidator(condition: any, successAction: any, formGroup: FormGroup): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const sourceControl = formGroup.get(condition.source);
    if (!sourceControl) {
      return null;
    }

    const sourceValue = sourceControl.value;
    let conditionMet = false;

    switch (condition.type) {
      case 'eq':
        conditionMet = sourceValue === condition.value;
        break;
      case 'neq':
        conditionMet = sourceValue !== condition.value;
        break;
    }

    if (conditionMet) {
      switch (successAction.validation_rule) {
        case 'notempty':
          return Validators.required(control);
        case 'txtaxpayerid':
          return Validators.pattern(/^\d{11}$/)(control);
        case 'fein':
          return Validators.pattern(/^\d{2}-?\d{7}$/)(control);
        case 'explainerquestion':
          if (successAction.arguments) {
            const dependentKeys = successAction.arguments
              .map((arg: any) => arg.question_code)
              .filter((key: any) => key !== undefined);
            return conditionalRequiredValidator(dependentKeys, formGroup)(control);
          }
          break;
      }
    }

    return null;
  };
}

export function layoutHasQuestions(layoutName: string, stateBond: CIPXQuoteRequestIntakeFormStateBond | null): boolean {
  if (stateBond === null || !stateBond.additional_questions || stateBond.additional_questions.length === 0) {
    return false;
  }

  // Check if any section contains question sets with the specified layout
  return stateBond.additional_questions.some(section =>
    section.question_sets.some(questionSet =>
      questionSet.question_set.layout === layoutName &&
      questionSet.questions && questionSet.questions.length > 0
    )
  );
}

export function sectionHasQuestions(sectionName: string, stateBond: CIPXQuoteRequestIntakeFormStateBond | null): boolean {
  if (stateBond === null || !stateBond.additional_questions || stateBond.additional_questions.length === 0) {
    return false;
  }

  // Check if the section exists and has question sets with questions
  const section = stateBond.additional_questions.find(s => s.section === sectionName);
  if (!section) {
    return false;
  }

  return section.question_sets.some(questionSet =>
    questionSet.questions && questionSet.questions.length > 0
  );
}

export function getQuestionsForSection(
  sectionName: string,
  stateBond: CIPXQuoteRequestIntakeFormStateBond
): CIPXQuoteRequestIntakeFormStateBondDetailedQuestion[] {
  if (!stateBond.additional_questions || stateBond.additional_questions.length === 0) {
    return [];
  }

  // Find the section and flatten all questions from all question sets within it
  const section = stateBond.additional_questions.find(s => s.section === sectionName);
  if (!section) {
    return [];
  }

  // Build a map of overrides from question_set.questions by question_code
  const overridesByCode = new Map<string, Partial<CIPXQuoteRequestIntakeFormStateBondDetailedQuestion>>();
  section.question_sets.forEach(questionSet => {
    if (!questionSet.question_set || !questionSet.question_set.questions) {
      return;
    }
    questionSet.question_set.questions.forEach(q => {
      const override: Partial<CIPXQuoteRequestIntakeFormStateBondDetailedQuestion> = {};
      if (q.question_text !== undefined) {
        override.question_text = q.question_text;
      }
      if (q.question_help_text !== undefined) {
        override.question_help_text = q.question_help_text;
      }
      if (q.question_label !== undefined) {
        override.question_label = q.question_label;
      }
      if (q.question_placeholder !== undefined) {
        override.question_placeholder = q.question_placeholder;
      }
      if (Object.keys(override).length > 0) {
        overridesByCode.set(q.question_code, {
          ...overridesByCode.get(q.question_code),
          ...override
        });
      }
    });
  });

  // Flatten detailed questions and apply overrides where provided
  return section.question_sets
    .filter(questionSet => questionSet.questions && questionSet.questions.length > 0)
    .flatMap(questionSet => questionSet.questions)
    .map(question => ({
      ...question,
      ...(overridesByCode.get(question.question_code) ?? {})
    }));
}

export function getQuestionsForLayout(
  layoutName: string,
  stateBond: CIPXQuoteRequestIntakeFormStateBond
): CIPXQuoteRequestIntakeFormStateBondDetailedQuestion[] {
  if (!stateBond.additional_questions || stateBond.additional_questions.length === 0) {
    return [];
  }

  // Build overrides from all sections' question_set definitions
  const overridesByCode = new Map<string, Partial<CIPXQuoteRequestIntakeFormStateBondDetailedQuestion>>();
  stateBond.additional_questions.forEach(section => {
    section.question_sets.forEach(questionSet => {
      if (!questionSet.question_set || !questionSet.question_set.questions) {
        return;
      }
      questionSet.question_set.questions.forEach(q => {
        const override: Partial<CIPXQuoteRequestIntakeFormStateBondDetailedQuestion> = {};
        if (q.question_text !== undefined) {
          override.question_text = q.question_text;
        }
        if (q.question_help_text !== undefined) {
          override.question_help_text = q.question_help_text;
        }
        if (q.question_label !== undefined) {
          override.question_label = q.question_label;
        }
        if (q.question_placeholder !== undefined) {
          override.question_placeholder = q.question_placeholder;
        }
        if (Object.keys(override).length > 0) {
          overridesByCode.set(q.question_code, {
            ...overridesByCode.get(q.question_code),
            ...override
          });
        }
      });
    });
  });

  // Filter to question sets with matching layout and apply overrides to their detailed questions
  return stateBond.additional_questions
    .flatMap(section => section.question_sets)
    .filter(questionSet =>
      questionSet.question_set.layout === layoutName &&
      questionSet.questions && questionSet.questions.length > 0
    )
    .flatMap(questionSet => questionSet.questions)
    .map(question => ({
      ...question,
      ...(overridesByCode.get(question.question_code) ?? {})
    }));
}

export function configureIntakeFormByPricingConfiguration(form: FormGroup<CIPXQuoteRequestIntakeForm>, configuration: CIPXQuoteRequestIntakeFormPricingCalculatorPricingConfiguration) {
  if(configuration.penalty && configuration.penalty.default_penalty) {
    form.controls.bondLookup.controls.bond_amount.setValue(configuration.penalty.default_penalty);
  } else {
    form.controls.bondLookup.controls.bond_amount.setValue(null);
  }


}
